import type { KeyboardEvent } from 'react';
import type { Exercise, WorkoutBlock, WorkoutWithBlocks } from '../../../../types/dataModel';
import {
  getWorkoutDisplayBannerUrl,
  isAllowlistedWorkoutBannerUrl,
} from '../../../../lib/workoutBannerImage';

export const V1_BLOCK_TYPE_OPTIONS = [
  { ui: 'Regular', db: 'Default' },
  { ui: 'Superset', db: 'Superset' },
  { ui: 'Circuit', db: 'Circuit' },
  { ui: 'AMRAP', db: 'AMRAP' },
] as const;

export function newExerciseGroupId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function rowGroupKeyForExerciseRun(s: {
  exerciseGroupId?: string | null;
  exerciseId?: string | null;
  emptyRunKey?: string | null;
}): string {
  if (s.exerciseGroupId != null && String(s.exerciseGroupId).trim() !== '') {
    return `group:${String(s.exerciseGroupId)}`;
  }
  if (s.exerciseId && String(s.exerciseId).trim() !== '') {
    return String(s.exerciseId);
  }
  if (s.emptyRunKey != null && String(s.emptyRunKey).trim() !== '') {
    return `empty:${String(s.emptyRunKey)}`;
  }
  return '__no_exercise__';
}

export function getConsecutiveRunIndicesForSet(exerciseSets: any[], setIndex: number): number[] {
  if (!exerciseSets?.length || setIndex < 0 || setIndex >= exerciseSets.length) return [setIndex];
  const key = rowGroupKeyForExerciseRun(exerciseSets[setIndex]);
  let start = setIndex;
  while (start > 0 && rowGroupKeyForExerciseRun(exerciseSets[start - 1]) === key) start--;
  let end = setIndex;
  while (end < exerciseSets.length - 1 && rowGroupKeyForExerciseRun(exerciseSets[end + 1]) === key) end++;
  const out: number[] = [];
  for (let i = start; i <= end; i++) out.push(i);
  return out;
}

export function syncExerciseRunsToBlockSetCount(block: any, targetSets: number): void {
  const n = Math.max(1, Math.min(20, Math.floor(Number(targetSets)) || 1));
  const arr = block.exerciseSets;
  if (!arr || !Array.isArray(arr)) return;

  const runs: any[][] = [];
  let cur: any[] = [];
  arr.forEach((s: any, idx: number) => {
    const key = rowGroupKeyForExerciseRun(s);
    const pk = idx > 0 ? rowGroupKeyForExerciseRun(arr[idx - 1]) : null;
    if (idx === 0 || key !== pk) {
      if (cur.length) runs.push(cur);
      cur = [s];
    } else {
      cur.push(s);
    }
  });
  if (cur.length) runs.push(cur);

  const out: any[] = [];
  runs.forEach((run) => {
    if (run.length > n) {
      out.push(...run.slice(0, n));
    } else if (run.length < n) {
      out.push(...run);
      const base = run[run.length - 1];
      for (let j = run.length; j < n; j++) {
        out.push({
          ...base,
          id: `temp-${Date.now()}-${j}-${Math.random()}`,
        });
      }
    } else {
      out.push(...run);
    }
  });
  block.exerciseSets = out;
  out.forEach((s: any, i: number) => {
    s.order = i;
  });
}

export type ExerciseRun = {
  groupKey: string;
  indices: number[];
  firstIndex: number;
  lastIndex: number;
  exercise: Exercise | undefined;
  sets: any[];
};

export function computeExerciseRuns(exerciseSets: any[]): ExerciseRun[] {
  if (!exerciseSets?.length) return [];
  const runs: ExerciseRun[] = [];
  let curKey = rowGroupKeyForExerciseRun(exerciseSets[0]);
  let curIndices: number[] = [0];

  for (let i = 1; i < exerciseSets.length; i++) {
    const key = rowGroupKeyForExerciseRun(exerciseSets[i]);
    if (key !== curKey) {
      runs.push({
        groupKey: curKey,
        indices: curIndices,
        firstIndex: curIndices[0],
        lastIndex: curIndices[curIndices.length - 1],
        exercise: exerciseSets[curIndices[0]]?.exercise,
        sets: curIndices.map((idx) => exerciseSets[idx]),
      });
      curKey = key;
      curIndices = [i];
    } else {
      curIndices.push(i);
    }
  }
  runs.push({
    groupKey: curKey,
    indices: curIndices,
    firstIndex: curIndices[0],
    lastIndex: curIndices[curIndices.length - 1],
    exercise: exerciseSets[curIndices[0]]?.exercise,
    sets: curIndices.map((idx) => exerciseSets[idx]),
  });
  return runs;
}

export function runHasUniformValue(sets: any[], measureType: 'reps' | 'duration'): boolean {
  if (sets.length <= 1) return true;
  if (measureType === 'duration') {
    const v = sets[0]?.durationSeconds;
    return sets.every((s) => s.durationSeconds === v);
  }
  const rt0 = sets[0]?.repText ?? (sets[0]?.rep != null ? String(sets[0].rep) : '');
  return sets.every((s) => {
    const rt = s.repText ?? (s.rep != null ? String(s.rep) : '');
    return rt === rt0;
  });
}

export function runHasUniformRest(sets: any[]): boolean {
  if (sets.length <= 1) return true;
  const v = sets[0]?.restSeconds ?? 0;
  return sets.every((s) => (s.restSeconds ?? 0) === v);
}

export function runHasUniformWeight(sets: any[]): boolean {
  if (sets.length <= 1) return true;
  const w0 = sets[0]?.weightKg;
  return sets.every((s) => {
    const w = s?.weightKg;
    if (w0 == null && w == null) return true;
    if (w0 == null || w == null) return false;
    return Number(w) === Number(w0);
  });
}

export function runMeasureKey(blockIndex: number, groupKey: string): string {
  return `${blockIndex}:${groupKey}`;
}

export function pruneExerciseMeasureTypesForBlock(
  prev: Record<string, 'reps' | 'duration'>,
  blockIndex: number,
): Record<string, 'reps' | 'duration'> {
  const prefix = `${blockIndex}:`;
  const next = { ...prev };
  Object.keys(next).forEach((k) => {
    const parts = k.split('-');
    if (parts.length === 2 && parseInt(parts[0], 10) === blockIndex) delete next[k];
    if (k.startsWith(prefix)) {
      // kept — caller may re-derive
    }
  });
  return next;
}

export const REPS_TEXT_FINAL_PATTERN = /^\d+(-\d+)?$/;

export function sanitizeRepsTextInput(raw: string): string {
  let s = raw.replace(/[^\d-]/g, '');
  s = s.replace(/^-+/, '');
  const i = s.indexOf('-');
  if (i === -1) return s;
  const a = s.slice(0, i).replace(/\D/g, '');
  const b = s.slice(i + 1).replace(/\D/g, '');
  return `${a}-${b}`;
}

export function normalizeRepsTextOnBlur(raw: string): string {
  return raw.trim().replace(/\s*-\s*/g, '-').replace(/\s/g, '');
}

export function formatSecondsToMMSS(totalSeconds: number | undefined | null): string {
  if (totalSeconds == null || Number.isNaN(totalSeconds) || totalSeconds <= 0) return '';
  const safe = Math.max(0, Math.floor(totalSeconds));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/** Parse compact digit runs as mm:ss (e.g. "0130" → 1:30, "130" → 1:30). */
function parseDigitsAsMmSs(digits: string): number | null {
  if (digits.length === 4) {
    const m = parseInt(digits.slice(0, 2), 10);
    const s = parseInt(digits.slice(2, 4), 10);
    if (Number.isNaN(m) || Number.isNaN(s) || s >= 60) return null;
    return m * 60 + s;
  }
  if (digits.length === 3) {
    const m = parseInt(digits.slice(0, 1), 10);
    const s = parseInt(digits.slice(1, 3), 10);
    if (Number.isNaN(m) || Number.isNaN(s) || s >= 60) return null;
    return m * 60 + s;
  }
  return null;
}

/**
 * Accepts "MM:SS" / "M:SS" (e.g. 01:30, 1:30), compact "0130", or plain seconds ("90").
 * Returns null for invalid input, undefined for empty input.
 */
export function parseDurationInputToSeconds(raw: string): number | undefined | null {
  const t = raw.trim();
  if (t === '') return undefined;
  if (t.includes(':')) {
    const parts = t.split(':');
    if (parts.length !== 2) return null;
    const mm = parts[0].trim();
    const ss = parts[1].trim();
    if (mm === '' || ss === '') return null;
    if (!/^\d+$/.test(mm) || !/^\d+$/.test(ss)) return null;
    const m = parseInt(mm, 10);
    const s = parseInt(ss, 10);
    if (Number.isNaN(m) || Number.isNaN(s) || s >= 60) return null;
    return m * 60 + s;
  }
  const digits = t.replace(/\D/g, '');
  if (digits.length >= 3 && digits.length <= 4 && /^\d+$/.test(digits)) {
    const mmss = parseDigitsAsMmSs(digits);
    if (mmss != null) return mmss;
  }
  if (!/^\d+$/.test(t)) return null;
  const v = parseInt(t, 10);
  if (Number.isNaN(v) || v < 0) return null;
  return v;
}

/** Blur input/textarea/select on Enter (Shift+Enter keeps newline in textareas). */
export function blurEditableOnEnter(e: KeyboardEvent<HTMLElement>): void {
  if (e.key !== 'Enter') return;
  const el = e.target;
  if (
    !(el instanceof HTMLInputElement) &&
    !(el instanceof HTMLTextAreaElement) &&
    !(el instanceof HTMLSelectElement)
  ) {
    return;
  }
  if (el instanceof HTMLTextAreaElement && e.shiftKey) return;
  if (
    el instanceof HTMLInputElement &&
    (el.type === 'submit' || el.type === 'button' || el.type === 'checkbox' || el.type === 'radio')
  ) {
    return;
  }
  e.preventDefault();
  el.blur();
}

/**
 * Formats duration while typing: inserts ":" after minutes (e.g. "0130" → "01:30").
 */

export function formatDurationInputWhileTyping(raw: string): string {
  if (raw.includes(':')) {
    const colonIdx = raw.indexOf(':');
    const mm = raw.slice(0, colonIdx).replace(/\D/g, '').slice(0, 2);
    const ss = raw.slice(colonIdx + 1).replace(/\D/g, '').slice(0, 2);
    return ss.length > 0 || raw.endsWith(':') ? `${mm}:${ss}` : mm;
  }
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;
  if (digits.length === 3) {
    if (digits.startsWith('0')) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    return `${digits.slice(0, 1)}:${digits.slice(1)}`;
  }
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
}

/** Normalize duration field text on blur to MM:SS when parseable. */
export function normalizeDurationInputOnBlur(raw: string): string {
  const parsed = parseDurationInputToSeconds(raw);
  if (parsed == null) return raw.trim();
  if (parsed === undefined) return '';
  return formatSecondsToMMSS(parsed);
}

export function isRegularBlock(type?: string): boolean {
  return !type || type === 'Default';
}

/** All sets share the same rep/repText, weightKg, and restSeconds (view-page parity). */
export function areSetsUniform(sets: { rep?: number | null; repText?: string | null; weightKg?: number | null; restSeconds?: number | null; durationSeconds?: number | null }[]): boolean {
  if (sets.length <= 1) return true;
  const repKey = (s: (typeof sets)[0]) => {
    if (s.repText != null && String(s.repText).trim() !== '') return `t:${s.repText}`;
    if (s.rep != null) return `n:${s.rep}`;
    if (s.durationSeconds != null && Number(s.durationSeconds) > 0) return `d:${s.durationSeconds}`;
    return '';
  };
  const r0 = repKey(sets[0]);
  const w0 = sets[0].weightKg ?? null;
  const rest0 = sets[0].restSeconds ?? null;
  return sets.every(
    (s) => repKey(s) === r0 && (s.weightKg ?? null) === w0 && (s.restSeconds ?? null) === rest0,
  );
}

export function formatBlockTypeLabel(type?: string): string {
  if (!type) return 'Regular';
  if (type === 'Default') return 'Regular';
  return String(type).replace(/_/g, ' ');
}

export function isV1BlockType(type?: string): boolean {
  const t = String(type || 'Default').toLowerCase();
  return t === 'default' || t === 'superset' || t === 'circuit' || t === 'amrap';
}

export function deriveWorkoutEquipmentAndMuscles(w: WorkoutWithBlocks): {
  equipment: string[];
  muscles: string[];
} {
  const equipMap = new Map<string, string>();
  const muscleMap = new Map<string, string>();
  for (const block of w.workoutBlocks ?? []) {
    for (const row of block.exerciseSets ?? []) {
      const ex = row.exercise;
      if (!ex) continue;
      const eqRaw = typeof ex.equipment === 'string' ? ex.equipment.trim() : '';
      if (eqRaw) {
        const key = eqRaw.toLowerCase();
        if (!equipMap.has(key)) equipMap.set(key, eqRaw);
      }
      const mgRaw = typeof ex.muscleGroup === 'string' ? ex.muscleGroup.trim() : '';
      if (mgRaw) {
        const key = mgRaw.toLowerCase();
        if (!muscleMap.has(key)) muscleMap.set(key, mgRaw);
      }
    }
  }
  return {
    equipment: Array.from(equipMap.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    ),
    muscles: Array.from(muscleMap.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    ),
  };
}

export function cloneWorkout(w: WorkoutWithBlocks): WorkoutWithBlocks {
  return JSON.parse(JSON.stringify(w)) as WorkoutWithBlocks;
}

export function countDirtyChanges(
  initial: WorkoutWithBlocks | null,
  edited: WorkoutWithBlocks | null,
  pendingBlockSetupIds: Set<string>,
): number {
  if (!initial || !edited) return 0;
  let n = 0;
  if (edited.name !== initial.name) n++;
  if ((edited.note || '') !== (initial.note || '')) n++;
  if (edited.estimatedDurationMinutes !== initial.estimatedDurationMinutes) n++;
  if (edited.difficulty !== initial.difficulty) n++;
  if ((edited.bannerImageUrl || '').trim() !== (initial.bannerImageUrl || '').trim()) n++;

  const ib = initial.workoutBlocks || [];
  const eb = edited.workoutBlocks || [];
  if (ib.length !== eb.length) return n + Math.max(1, Math.abs(eb.length - ib.length));

  for (let i = 0; i < eb.length; i++) {
    const initialBlock = ib[i];
    const editedBlock = eb[i];
    if (!initialBlock) {
      n++;
      continue;
    }
    const bid = editedBlock.id || `${i}`;
    if (pendingBlockSetupIds.has(bid)) {
      n++;
      continue;
    }
    if (initialBlock.name !== editedBlock.name) n++;
    if ((initialBlock.note || '') !== (editedBlock.note || '')) n++;
    if (initialBlock.type !== editedBlock.type) n++;
    if (initialBlock.sets !== editedBlock.sets) n++;

    const is = initialBlock.exerciseSets || [];
    const es = editedBlock.exerciseSets || [];
    if (is.length !== es.length) {
      n += Math.max(1, Math.abs(es.length - is.length));
      continue;
    }
    for (let j = 0; j < es.length; j++) {
      const a = is[j];
      const b = es[j];
      if (!a) {
        n++;
        continue;
      }
      if (
        a.exerciseId !== b.exerciseId ||
        a.rep !== b.rep ||
        (a as any).repText !== (b as any).repText ||
        a.durationSeconds !== b.durationSeconds ||
        a.restSeconds !== b.restSeconds ||
        (a.note || '') !== (b.note || '') ||
        (a as any).weightKg !== (b as any).weightKg
      ) {
        n++;
      }
    }
  }
  return n;
}

export function hasUnsavedChanges(
  initial: WorkoutWithBlocks | null,
  edited: WorkoutWithBlocks | null,
  pendingBlockSetupIds: Set<string>,
): boolean {
  return countDirtyChanges(initial, edited, pendingBlockSetupIds) > 0;
}

export type SanitizeWorkoutOptions = {
  pendingBlockSetupIds: Set<string>;
  /** When true, skip requiring an allowlisted banner (create follow-up RPC after createWorkout). */
  bannerOptional?: boolean;
};

export function sanitizeWorkoutForSave(
  toSave: WorkoutWithBlocks,
  opts: SanitizeWorkoutOptions,
): { payload: WorkoutWithBlocks; nameError?: string; bannerError?: string } {
  const payload = cloneWorkout(toSave);
  const nameTrimmed = (payload.name || '').trim();
  if (!nameTrimmed) {
    return { payload, nameError: 'Workout name is required.' };
  }
  payload.name = nameTrimmed;
  let bannerTrim = (payload.bannerImageUrl || '').trim();
  if (!isAllowlistedWorkoutBannerUrl(bannerTrim)) {
    bannerTrim = getWorkoutDisplayBannerUrl({
      id: payload.id,
      bannerImageUrl: bannerTrim || undefined,
    }).trim();
  }
  if (!isAllowlistedWorkoutBannerUrl(bannerTrim)) {
    if (opts.bannerOptional) {
      payload.bannerImageUrl = bannerTrim || undefined;
    } else {
      return {
        payload,
        bannerError: 'Choose a cover image from the gallery before saving.',
      };
    }
  } else {
    payload.bannerImageUrl = bannerTrim;
  }

  if (Array.isArray(payload.workoutBlocks)) {
    payload.workoutBlocks = (payload.workoutBlocks as any[])
      .filter((b: WorkoutBlock, idx: number) => {
        const id = b?.id || `${idx}`;
        return !opts.pendingBlockSetupIds.has(id);
      })
      .map((b: any) => {
        const copy = { ...b };
        const t = String(copy.type || '').toLowerCase();
        if (t === 'amrap' && (copy.sets == null || !Number.isFinite(Number(copy.sets)) || Number(copy.sets) < 1)) {
          copy.sets = 1;
        }
        const noteTrim = (copy.note || '').trim();
        copy.note = noteTrim === '' ? undefined : noteTrim;
        if (Array.isArray(copy.exerciseSets)) {
          copy.exerciseSets = copy.exerciseSets
            .filter((s: any) => String(s?.exerciseId || '').trim() !== '')
            .map((s: any) => {
              const row = { ...s } as any;
              delete row.emptyRunKey;
              return row;
            });
          copy.exerciseSets.forEach((s: any, i: number) => {
            s.order = i;
          });
        }
        return copy;
      });
  }
  return { payload };
}

/** True when a freshly loaded workout likely reflects a just-saved payload (for save recovery). */
export function workoutPersistedMatchesPayload(
  payload: WorkoutWithBlocks,
  fetched: WorkoutWithBlocks,
): boolean {
  if ((payload.name || '').trim() !== (fetched.name || '').trim()) return false;
  const pb = payload.workoutBlocks ?? [];
  const fb = fetched.workoutBlocks ?? [];
  if (pb.length !== fb.length) return false;
  for (let i = 0; i < pb.length; i++) {
    if ((pb[i].name || '').trim() !== (fb[i].name || '').trim()) return false;
    if (String(pb[i].type || 'Default') !== String(fb[i].type || 'Default')) return false;
    const pCount = (pb[i].exerciseSets ?? []).filter((s) => String(s?.exerciseId || '').trim()).length;
    const fCount = (fb[i].exerciseSets ?? []).filter((s) => String(s?.exerciseId || '').trim()).length;
    if (pCount !== fCount) return false;
  }
  const updatedAt = fetched.updatedAt ? new Date(fetched.updatedAt).getTime() : 0;
  if (!updatedAt || Date.now() - updatedAt > 120_000) return false;
  return true;
}

/** Default set rows when adding exercise(s) to a block. */
export function defaultSetCountForBlock(block: WorkoutBlock): number {
  const tl = String(block.type || 'Default').toLowerCase();
  if (tl === 'circuit' || tl === 'superset') {
    return Math.max(1, Math.min(20, Math.floor(Number(block.sets)) || 1));
  }
  if (tl === 'amrap') return 1;
  return Math.max(1, Math.min(20, Math.floor(Number(block.sets)) || 1));
}

export function appendExerciseRuns(
  block: WorkoutBlock & { exerciseSets?: any[] },
  blockIndex: number,
  exercises: Exercise[],
): void {
  if (!block.exerciseSets) block.exerciseSets = [];
  const n = defaultSetCountForBlock(block);
  for (const exercise of exercises) {
    const runId = newExerciseGroupId();
    for (let s = 0; s < n; s++) {
      block.exerciseSets.push({
        id: `temp-${Date.now()}-${Math.random()}`,
        blockId: block.id || `${blockIndex}`,
        exerciseId: exercise.id,
        exercise,
        exerciseGroupId: runId,
        order: block.exerciseSets.length,
        rep: undefined,
        durationSeconds: undefined,
        restSeconds: 0,
        note: undefined,
      } as any);
    }
  }
  block.exerciseSets.forEach((set, idx) => {
    set.order = idx;
  });
}

export function createBlockFromModalInput(name: string, structureDb: string): any {
  const tl = structureDb.toLowerCase();
  let sets = 1;
  if (tl === 'circuit' || tl === 'superset') sets = 3;
  return {
    id: `temp-${Date.now()}-${Math.random()}`,
    name: name.trim() || 'New block',
    type: structureDb,
    sets,
    exerciseSets: [],
    note: undefined,
    restSeconds: 0,
  };
}

/** Local draft for full-page create — no DB row until publish. */
export function createEmptyWorkoutDraft(): WorkoutWithBlocks {
  const now = new Date().toISOString();
  return {
    id: '',
    name: '',
    note: undefined,
    estimatedDurationMinutes: undefined,
    difficulty: undefined,
    bannerImageUrl: undefined,
    createdBy: null,
    isPublic: false,
    createdAt: now,
    updatedAt: now,
    workoutBlocks: [],
  };
}

/** Create-page required fields (product update 2026-07-05). */
export type CreateRequiredField = 'name' | 'level' | 'duration' | 'cover';

export const CREATE_REQUIRED_FIELD_LABELS: Record<CreateRequiredField, string> = {
  name: 'Name',
  level: 'Level',
  duration: 'Duration',
  cover: 'Cover image',
};

export function getCreateMissingRequiredFields(
  w: WorkoutWithBlocks,
  bannerUrl: string,
): CreateRequiredField[] {
  const missing: CreateRequiredField[] = [];
  if (!(w.name || '').trim()) missing.push('name');
  if (!w.difficulty) missing.push('level');
  if (w.estimatedDurationMinutes == null || w.estimatedDurationMinutes < 1) missing.push('duration');
  const banner = bannerUrl.trim();
  if (!banner || !isAllowlistedWorkoutBannerUrl(banner)) missing.push('cover');
  return missing;
}

export function isCreateReady(w: WorkoutWithBlocks, bannerUrl: string): boolean {
  return getCreateMissingRequiredFields(w, bannerUrl).length === 0;
}
